/**
 * Event Settings Helper Utilities
 * Provides standardized data initialization and handling for Event Settings forms
 */

/**
 * Gets a field value with proper priority fallback
 * Priority: event.settings.field > settings.field.value > defaultValue
 */
export const getFieldValue = (
  fieldName: string,
  eventData: any,
  settingsData: any,
  defaultValue: any = null
): any => {
  // First priority: event.settings field (most current, must not be null or undefined)
  if (eventData?.settings?.[fieldName] !== undefined && eventData?.settings?.[fieldName] !== null) {
    return eventData.settings[fieldName];
  }

  // Second priority: settings API response field
  if (settingsData?.settings?.[fieldName]?.value !== undefined) {
    return settingsData.settings[fieldName].value;
  }

  // Third priority: direct event field (for core event data, must not be null or undefined)
  if (eventData?.[fieldName] !== undefined && eventData?.[fieldName] !== null) {
    return eventData[fieldName];
  }

  // Final fallback: default value
  return defaultValue;
};

/**
 * Gets a core event field value (name, dates, location, etc.)
 * Priority: event.field > settings.field.value > defaultValue
 */
export const getCoreEventFieldValue = (
  fieldName: string,
  eventData: any,
  settingsData: any,
  defaultValue: any = null
): any => {
  // First priority: direct event field (must not be null or undefined)
  if (eventData?.[fieldName] !== undefined && eventData?.[fieldName] !== null) {
    return eventData[fieldName];
  }

  // Second priority: settings API response field
  if (settingsData?.settings?.[fieldName]?.value !== undefined) {
    return settingsData.settings[fieldName].value;
  }

  // Final fallback: default value
  return defaultValue;
};

/**
 * Safely parses JSON fields that might be strings or objects
 */
export const parseJsonField = (value: any): any => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return value;
};

/**
 * Validates that end date/time is after start date/time
 */
export const validateDateTimeRange = (
  startDate: any,
  startTime: any,
  endDate: any,
  endTime: any
): boolean => {
  if (!startDate || !endDate) return true; // Allow empty dates

  const start = startTime
    ? startDate.hour(startTime.hour()).minute(startTime.minute())
    : startDate.startOf('day');

  const end = endTime
    ? endDate.hour(endTime.hour()).minute(endTime.minute())
    : endDate.endOf('day');

  return end.isAfter(start);
};

/**
 * Combines date and time into ISO string with proper timezone handling
 */
export const combineDateTimeToISO = (
  date: any,
  time: any,
  defaultToStartOfDay: boolean = false
): string | null => {
  if (!date) return null;

  if (time) {
    return date
      .hour(time.hour())
      .minute(time.minute())
      .second(time.second())
      .toISOString();
  }

  return defaultToStartOfDay
    ? date.startOf('day').toISOString()
    : date.endOf('day').toISOString();
};

/**
 * Normalizes i18n.language (e.g. "vi-VN", "en-US", "vi", "en") into the plain "vi"/"en" key used
 * inside multi-language field objects ({ en: "...", vi: "..." }). Same de-facto pattern already
 * used across ~20 form pages (`i18n.language?.startsWith('vi') ? 'vi' : 'en'`) — centralized here
 * so display code (list pages) can share it instead of re-deriving it inline everywhere.
 */
export const resolveUILanguage = (rawLanguage?: string): 'en' | 'vi' =>
  rawLanguage?.startsWith('vi') ? 'vi' : 'en';

/**
 * Extracts text from multi-language objects
 * Priority: specified language > 'en' > first available language > fallback
 */
export const getMultiLanguageText = (
  value: any,
  preferredLanguage: string = 'en',
  fallback: string = ''
): string => {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    // Try preferred language first
    if (value[preferredLanguage]) return value[preferredLanguage];
    // Try English as default
    if (value.en) return value.en;
    // Get first available language
    const firstValue = Object.values(value)[0];
    if (typeof firstValue === 'string') return firstValue;
  }
  return fallback;
};

/**
 * Standardized form field initialization for Event Settings
 */
export const initializeFormFields = (
  eventData: any,
  settingsData: any,
  fieldMappings: Record<string, any>
): Record<string, any> => {
  const formValues: Record<string, any> = {};

  for (const [formField, config] of Object.entries(fieldMappings)) {
    const {
      dataField = formField,
      isEventField = false,
      defaultValue = null,
      parser = null
    } = config;

    let value;
    if (isEventField) {
      value = getCoreEventFieldValue(dataField, eventData, settingsData, defaultValue);
    } else {
      value = getFieldValue(dataField, eventData, settingsData, defaultValue);
    }

    // Apply custom parser if provided
    if (parser && value !== null && value !== undefined) {
      value = parser(value);
    }

    formValues[formField] = value;
  }

  return formValues;
};